import React, { ReactElement, UIEvent, useEffect, useRef } from 'react';

import { SupportMessage } from '../../../../../types/entity.types';

import { getClassNames, retrieveFromLocalStorage } from '../../../../../utils/util';

import './support-chat-box.scss';

type Props = {
  messages: SupportMessage[];
  emptyDateLabel?: string;
};

const SupportChatBox = ({ messages, emptyDateLabel }: Props) => {
  const endOfMessages = useRef<HTMLDivElement | null>(null);
  const currentUser = useRef<string>(retrieveFromLocalStorage('user'));
  const hasUserScrolled = useRef(false);

  useEffect(() => {
    if (!hasUserScrolled.current) {
      endOfMessages.current?.scrollIntoView({ block: 'end', inline: 'nearest' });
    }
  }, [messages]);

  const handleScroll = (scrollEvent: UIEvent<HTMLDivElement>) => {
    hasUserScrolled.current =
      scrollEvent.currentTarget.scrollHeight - scrollEvent.currentTarget.scrollTop !==
      scrollEvent.currentTarget.clientHeight;
  };

  const renderMessages = () => {
    const dayMap = new Map<string, SupportMessage[]>();

    const reversedMessages = [...messages].reverse();

    reversedMessages.forEach((message) => {
      const { date } = message;
      const dayMessages = dayMap.get(date);
      if (!dayMessages) {
        dayMap.set(date, [message]);
      } else {
        dayMessages.push(message);
      }
    });

    const elements: ReactElement[] = [];

    dayMap.forEach((value, key) => {
      elements.push(
        <div key={key}>
          <div className="support-chat-box__date-bubble">
            <div>{value[0]?.date}</div>
          </div>
          {value.map((message, index) => {
            const right = message.userExternalReference === currentUser.current;

            return (
              <div className={getClassNames('support-chat-box__bubble-container', { right })} key={index}>
                <div className={getClassNames('support-chat-box__bubble', { right })}>
                  <p className={getClassNames('support-chat-box__bubble__content', { right })}>{message.message}</p>
                  <p className={getClassNames('support-chat-box__bubble__time', { right })}>{message.time}</p>
                </div>
              </div>
            );
          })}
        </div>
      );
    });

    return elements;
  };

  return (
    <div className="support-chat-box" onScroll={handleScroll}>
      {messages.length > 0 && renderMessages()}
      {messages.length === 0 && emptyDateLabel && (
        <div className="support-chat-box__date-bubble">
          <div>{emptyDateLabel}</div>
        </div>
      )}
      {messages.length === 0 && !emptyDateLabel && <span className="support-chat-box__no-messages">No Messages</span>}
      <div ref={endOfMessages} />
    </div>
  );
};

export default SupportChatBox;
